import axiosInstance from '../api/axios.config';

export type MfaDeliveryChannel = 'email' | 'sms';
export type OryMfaVia = 'email' | 'phone';
export type MfaPreferenceSource = 'stored' | 'initialized' | 'fallback';

export interface MfaPreferenceResponse {
  channel: MfaDeliveryChannel;
  oryVia: OryMfaVia;
  source: MfaPreferenceSource;
  availableChannels: MfaDeliveryChannel[];
  verifiedChannels: MfaDeliveryChannel[];
  smsConsent: boolean;
  updatedAt: string | null;
}

const isChannel = (value: unknown): value is MfaDeliveryChannel =>
  value === 'email' || value === 'sms';

export const parseMfaPreference = (value: unknown): MfaPreferenceResponse => {
  if (typeof value !== 'object' || value === null)
    throw new Error('Invalid MFA preference response');
  const response = value as Record<string, unknown>;
  const expectedVia =
    response.channel === 'email' ? 'email' : response.channel === 'sms' ? 'phone' : null;
  const validSource =
    response.source === 'stored' ||
    response.source === 'initialized' ||
    response.source === 'fallback';
  if (
    !isChannel(response.channel) ||
    response.oryVia !== expectedVia ||
    !validSource ||
    !Array.isArray(response.availableChannels) ||
    !response.availableChannels.every(isChannel) ||
    !Array.isArray(response.verifiedChannels) ||
    !response.verifiedChannels.every(isChannel) ||
    typeof response.smsConsent !== 'boolean' ||
    (response.updatedAt !== null && typeof response.updatedAt !== 'string')
  ) {
    throw new Error('Invalid MFA preference response');
  }
  return response as unknown as MfaPreferenceResponse;
};

export const getMfaPreference = async (): Promise<MfaPreferenceResponse> =>
  parseMfaPreference(await axiosInstance.get<unknown, unknown>('/auth/mfa-preference'));

export const updateMfaPreference = async (
  channel: MfaDeliveryChannel
): Promise<MfaPreferenceResponse> =>
  parseMfaPreference(
    await axiosInstance.put<unknown, unknown, { channel: MfaDeliveryChannel }>(
      '/auth/mfa-preference',
      { channel }
    )
  );
